/**
 * Project and grant accounting.
 *
 * A grant is a promise with conditions attached. What matters is not just what
 * has been spent but what has been spent AGAINST THE AWARD, including what is
 * committed — because a funder will not pay an overspend, and a commitment is
 * an overspend that has not arrived yet.
 */

import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTerms } from '../lib/tenant';
import { date, money, percent, statusLabel, statusTone, utilisationTextClass } from '../lib/format';
import { AsyncPanel, Badge, Card, DataTable, Money, PageHeader, Progress, Stat } from '../components/ui';

/**
 * One row of GET /reports/project-accounting.
 *
 * These are the server's own keys, and only the server's keys. The route sends
 * its query straight out in snake_case and converts every money column to a
 * whole number of pence on the way, so nothing here needs a Number() or a
 * second spelling to fall back on. Carrying camelCase alternates "just in case"
 * is how income, expenditure and commitment came to be blank on every row: a
 * key nobody sends reads as undefined, and undefined renders as nothing at all.
 */
interface ProjectRow {
  id: string;
  code: string;
  name: string;
  project_type: string;
  status: string;
  funder_name: string | null;
  funding_reference: string | null;
  grant_type: string | null;
  award_amount_minor: number;
  start_date: string | null;
  end_date: string | null;
  manager_name: string | null;
  expenditure_minor: number;
  income_minor: number;
  commitment_minor: number;
  budget_minor: number;
  available_minor: number;
  net_position_minor: number;
  /** Award less what has been spent. Null where there is no external award. */
  award_remaining_minor: number | null;
  utilisation_percent: number | null;
}

interface ProjectAccounting {
  meta: { reportName: string; runBy: string; runAtDisplay: string; dataNotice: string };
  projects: ProjectRow[];
}

/**
 * How much of the funder's award has been used.
 *
 * Deliberately not the server's `utilisation_percent`, which measures spend
 * against the INTERNAL budget for the project — a different and usually much
 * larger denominator. The question this column answers is the funder's one:
 * of the money we were awarded, how much is spoken for, counting commitments
 * that have not yet reached an invoice.
 */
function awardUsedPercent(p: ProjectRow): number | null {
  if (p.award_amount_minor <= 0) return null;
  return ((p.expenditure_minor + p.commitment_minor) / p.award_amount_minor) * 100;
}

/** The funder line under a project name: who it is, and their reference for it. */
function funderLine(p: ProjectRow): string {
  if (!p.funder_name) return 'No external funder';
  return p.funding_reference ? `${p.funder_name} · ${p.funding_reference}` : p.funder_name;
}

export function ProjectsPage() {
  const t = useTerms();
  const { can } = useSession();
  const report = useAsync(() => api.get<ProjectAccounting>('/reports/project-accounting'), []);

  /*
   * "What has that award actually been spent on?" is the question this screen
   * invites and could not answer. There is no project detail route, but the
   * ledger enquiry already takes a project filter, so the award code opens the
   * postings behind it. Both that and the header link land in the report
   * runner, so neither is drawn for someone who may not run a report: a control
   * that ends on a refusal is worse than no control at all.
   */
  const canDrill = can('report.run');

  return (
    <>
      <PageHeader
        title={t.projectPlural}
        subtitle="Income, expenditure and commitments against each award."
        actions={
          canDrill ? (
            /* The same figures in the report runner, which is where the export,
               the print layout and the "run by, at, against" header live. */
            <Link className="btn btn--sm" to="/reports/PROJECT_ACCOUNTING">
              Open as a report
            </Link>
          ) : null
        }
      />

      <AsyncPanel state={report}>
        {(data) => {
          const rows = data.projects;
          // Summed here only to display the total of the column the reader is
          // already looking at; every figure in it was computed by the server.
          const sum = (pick: (p: ProjectRow) => number | null) => rows.reduce((total, p) => total + (pick(p) ?? 0), 0);
          const awarded = sum((p) => p.award_amount_minor);
          const income = sum((p) => p.income_minor);
          const spent = sum((p) => p.expenditure_minor);
          const committed = sum((p) => p.commitment_minor);
          const remaining = sum((p) => p.award_remaining_minor);
          const funded = rows.filter((p) => p.award_amount_minor > 0).length;

          return (
            <div className="stack">
              {/*
                The tiles carry no drill of their own: the way through to the
                records is one row down, because the ledger enquiry filters by
                a single project and there is no route that means "every
                posting against every award at once".
              */}
              <div className="grid grid--4">
                <Stat
                  label="Awarded"
                  value={money(awarded)}
                  detail={`${funded} of ${rows.length} ${lower(rows.length === 1 ? t.project : t.projectPlural)} funded by an external award`}
                />
                <Stat label="Income received" value={money(income)} detail="Claimed from funders and posted" />
                <Stat
                  label="Spent and committed"
                  value={money(spent + committed)}
                  detail={`${money(spent)} spent · ${money(committed)} committed`}
                />
                <Stat label="Award remaining" value={money(remaining)} detail="Award less what has been spent" />
              </div>

              <Card
                title={`${t.projectPlural} and grants`}
                subtitle="Committed means ordered and not yet invoiced. It counts against the award just as spend does."
                flush
              >
                <DataTable
                  caption={`${t.projectPlural} with their award position`}
                  rows={rows}
                  rowKey={(p) => p.id}
                  emptyMessage={`No ${lower(t.projectPlural)} are recorded.`}
                  /*
                   * Sortable but not searchable: a register of this length is
                   * read whole, and a search box would leave the total row
                   * below sitting under a filtered handful of rows and
                   * contradicting them.
                   */
                  columns={[
                    {
                      key: 'code',
                      header: 'Code',
                      width: '9rem',
                      sortValue: (p) => p.code,
                      render: (p) => (
                        <>
                          {canDrill ? (
                            <Link to={`/reports/GENERAL_LEDGER?projectId=${p.id}`} title={`Every posting coded to ${p.code}`}>
                              <span className="mono nowrap">{p.code}</span>
                            </Link>
                          ) : (
                            <span className="mono nowrap">{p.code}</span>
                          )}
                          <div className="smaller muted">{statusLabel(p.project_type)}</div>
                        </>
                      ),
                    },
                    {
                      key: 'name',
                      header: t.project,
                      sortValue: (p) => p.name,
                      render: (p) => (
                        <>
                          {/* Bounded, because an unbounded nowrap string sets
                              the column's minimum width: a funder named
                              "Department for Education — FE Capital
                              Transformation Fund" pushed the award figures off
                              the right-hand edge of the table. The full text
                              stays available on hover. */}
                          <div className="truncate" style={{ maxWidth: '22rem' }} title={p.name}>
                            {p.name}
                          </div>
                          <div className="smaller muted truncate" style={{ maxWidth: '22rem' }} title={funderLine(p)}>
                            {funderLine(p)}
                          </div>
                        </>
                      ),
                    },
                    {
                      key: 'period',
                      header: 'Period',
                      width: '11rem',
                      sortValue: (p) => p.start_date,
                      render: (p) => (
                        <span className="smaller nowrap">
                          {date(p.start_date)} — {date(p.end_date)}
                        </span>
                      ),
                    },
                    {
                      key: 'award',
                      header: 'Award',
                      numeric: true,
                      sortValue: (p) => p.award_amount_minor,
                      render: (p) => (
                        <>
                          <Money minor={p.award_amount_minor} blankIfZero />
                          {/* Capital and revenue awards are accounted for
                              differently — a capital grant is deferred and
                              released as the asset depreciates — so the kind of
                              award belongs beside its figure. */}
                          {p.grant_type ? <div className="smaller muted">{statusLabel(p.grant_type)} grant</div> : null}
                        </>
                      ),
                    },
                    {
                      key: 'income',
                      header: 'Income',
                      numeric: true,
                      sortValue: (p) => p.income_minor,
                      render: (p) => <Money minor={p.income_minor} blankIfZero />,
                    },
                    {
                      key: 'spend',
                      header: 'Spent',
                      numeric: true,
                      sortValue: (p) => p.expenditure_minor,
                      render: (p) => <Money minor={p.expenditure_minor} blankIfZero />,
                    },
                    {
                      key: 'committed',
                      header: 'Committed',
                      numeric: true,
                      sortValue: (p) => p.commitment_minor,
                      render: (p) => <Money minor={p.commitment_minor} blankIfZero />,
                    },
                    {
                      key: 'used',
                      header: 'Award used',
                      width: '10rem',
                      sortValue: (p) => awardUsedPercent(p),
                      render: (p) => {
                        const used = awardUsedPercent(p);
                        if (used === null) return <span className="muted smaller">No award recorded</span>;
                        return (
                          <>
                            <Progress percent={used} label={`${p.name}: award used`} />
                            <span className={`smaller ${utilisationTextClass(used)}`}>{percent(used)}</span>
                          </>
                        );
                      },
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      sortValue: (p) => p.status,
                      render: (p) => <Badge tone={statusTone(p.status)}>{statusLabel(p.status)}</Badge>,
                    },
                  ]}
                  footer={
                    <tr className="is-total">
                      <td colSpan={3}>Total</td>
                      <td className="num">{money(awarded)}</td>
                      <td className="num">{money(income)}</td>
                      <td className="num">{money(spent)}</td>
                      <td className="num">{money(committed)}</td>
                      <td colSpan={2} />
                    </tr>
                  }
                />
              </Card>

              <p className="smaller muted">
                <strong>Award used</strong> is spend plus commitments as a share of the funder&rsquo;s award, not of the
                internal budget, because it is the award the funder will pay against.{' '}
                {canDrill ? <>Select an award code to see every posting coded to it. </> : null}
                {data.meta.dataNotice}
              </p>
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}
